import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { Mic, MicOff, Trash2, X, Volume2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Frank's avatar component
const FrankAvatar = ({ size = 48 }) => (
  <div 
    className="frank-avatar"
    style={{ width: size, height: size }}
    data-testid="frank-avatar"
  >
    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Face */}
      <circle cx="50" cy="50" r="45" fill="#F5E6D3"/>
      {/* Hair */}
      <path d="M20 35 Q50 10 80 35 Q75 25 50 20 Q25 25 20 35" fill="#4A3728"/>
      {/* Left eye */}
      <ellipse cx="35" cy="45" rx="6" ry="7" fill="#2C2A29"/>
      {/* Right eye - winking */}
      <path d="M58 45 Q65 42 72 45" stroke="#2C2A29" strokeWidth="3" strokeLinecap="round"/>
      {/* Eyebrow left */}
      <path d="M26 36 Q35 32 44 36" stroke="#4A3728" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Eyebrow right - raised */}
      <path d="M56 33 Q65 29 74 34" stroke="#4A3728" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Smirk */}
      <path d="M35 65 Q50 75 70 62" stroke="#2C2A29" strokeWidth="3" strokeLinecap="round" fill="none"/>
      {/* Slight cheek */}
      <circle cx="75" cy="55" r="6" fill="#E8C4B8" opacity="0.5"/>
    </svg>
  </div>
);

// Macro card component
const MacroCard = ({ label, value, unit, color }) => (
  <div className="macro-card" data-testid={`macro-${label.toLowerCase()}`}>
    <span className="macro-label">{label}</span>
    <span className="macro-value" style={{ color }}>
      {value}<span className="macro-unit">{unit}</span>
    </span>
  </div>
);

// Food item component
const FoodItem = ({ food, onDelete }) => (
  <div className="food-item" data-testid={`food-item-${food.id}`}>
    <div className="food-info">
      <span className="food-name">{food.name}</span>
      <span className="food-macros">
        P: {food.protein}g | C: {food.carbs}g | F: {food.fat}g
      </span>
    </div>
    <div className="food-actions">
      <span className="food-calories">{food.calories} cal</span>
      <button 
        className="delete-btn"
        onClick={() => onDelete(food.id)}
        data-testid={`delete-food-${food.id}`}
        aria-label="Delete food"
      >
        <Trash2 size={18} />
      </button>
    </div>
  </div>
);

function App() {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dailyLog, setDailyLog] = useState(null);
  const [frankMessage, setFrankMessage] = useState("Tap the mic and tell me what you ate. No BS.");
  const [error, setError] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [showTextInput, setShowTextInput] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Load today's log on mount
  useEffect(() => {
    loadTodayLog();
  }, []);

  const loadTodayLog = async () => {
    try {
      const response = await axios.get(`${API}/today`);
      setDailyLog(response.data);
    } catch (err) {
      console.error("Failed to load today's log:", err);
      setError("Couldn't load your data. Try refreshing.");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await processAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsListening(true);
      setFrankMessage("Listening...");
    } catch (err) {
      console.error("Microphone error:", err);
      setError("Can't access your mic. Check permissions.");
      setShowTextInput(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
      setIsProcessing(true);
      setFrankMessage("Processing...");
    }
  };

  const processAudio = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFrankMessage(response.data.message);
      
      if (response.data.food_item) {
        await loadTodayLog();
      }
    } catch (err) {
      console.error("Processing error:", err);
      setFrankMessage("Something went wrong. Try again or type it out.");
      setShowTextInput(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!textInput.trim()) return;

    setIsProcessing(true);
    setFrankMessage("Processing...");

    try {
      const response = await axios.post(`${API}/food/text`, { text: textInput });
      setFrankMessage(response.data.message);
      
      if (response.data.food_item) {
        await loadTodayLog();
      }
      setTextInput("");
    } catch (err) {
      console.error("Text processing error:", err);
      setFrankMessage("Couldn't process that. Try being more specific.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteFood = async (foodId) => {
    try {
      const response = await axios.delete(`${API}/food/${foodId}`);
      setFrankMessage(response.data.message);
      await loadTodayLog();
    } catch (err) {
      console.error("Delete error:", err);
      setFrankMessage("Couldn't delete that. Try again.");
    }
  };

  const handleClearDay = async () => {
    if (!window.confirm("Clear everything from today? This can't be undone.")) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.delete(`${API}/daily/${today}/clear`);
      setFrankMessage(response.data.message);
      await loadTodayLog();
    } catch (err) {
      console.error("Clear error:", err);
      setFrankMessage("Couldn't clear the day. Try again.");
    }
  };

  const handleMicClick = () => {
    if (isProcessing) return;
    
    if (isListening) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const totals = dailyLog || {
    total_calories: 0,
    total_protein: 0,
    total_carbs: 0,
    total_fat: 0,
    total_fiber: 0,
    foods: []
  };

  return (
    <div className="app-container" data-testid="frank-app">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <FrankAvatar size={44} />
          <div className="header-text">
            <h1>FRANK</h1>
            <span className="tagline">No sugar-coating</span>
          </div>
        </div>
        {totals.foods?.length > 0 && (
          <button 
            className="clear-btn"
            onClick={handleClearDay}
            data-testid="clear-day-btn"
            aria-label="Clear today"
          >
            <X size={20} />
          </button>
        )}
      </header>

      {/* Main calorie display */}
      <section className="calorie-section" data-testid="calorie-display">
        <div className="calorie-circle">
          <span className="calorie-number" data-testid="total-calories">
            {totals.total_calories}
          </span>
          <span className="calorie-label">calories today</span>
        </div>
      </section>

      {/* Macro grid */}
      <section className="macro-grid" data-testid="macro-grid">
        <MacroCard label="Protein" value={totals.total_protein} unit="g" color="#7A9E7E" />
        <MacroCard label="Carbs" value={totals.total_carbs} unit="g" color="#DDA77B" />
        <MacroCard label="Fat" value={totals.total_fat} unit="g" color="#E86A58" />
        <MacroCard label="Fiber" value={totals.total_fiber} unit="g" color="#8B7355" />
      </section>

      {/* Frank's message */}
      <section className="frank-section">
        <div className="frank-bubble" data-testid="frank-message">
          <FrankAvatar size={32} />
          <p>{frankMessage}</p>
        </div>
      </section>

      {/* Text input (fallback) */}
      {showTextInput && (
        <form className="text-input-form" onSubmit={handleTextSubmit}>
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Type what you ate..."
            className="text-input"
            data-testid="food-text-input"
            disabled={isProcessing}
          />
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isProcessing || !textInput.trim()}
            data-testid="submit-text-btn"
          >
            Log
          </button>
        </form>
      )}

      {/* Food log */}
      <section className="food-log" data-testid="food-log">
        <div className="food-log-header">
          <h2>Today's Log</h2>
          {!showTextInput && (
            <button 
              className="text-mode-btn"
              onClick={() => setShowTextInput(true)}
              data-testid="show-text-input-btn"
            >
              Type instead
            </button>
          )}
        </div>
        {totals.foods?.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <p>Nothing logged yet. Tap the mic to start.</p>
          </div>
        ) : (
          <div className="food-list">
            {totals.foods?.map((food) => (
              <FoodItem 
                key={food.id} 
                food={food} 
                onDelete={handleDeleteFood}
              />
            ))}
          </div>
        )}
      </section>

      {/* Mic button */}
      <button
        className={`mic-button ${isListening ? 'listening' : ''} ${isProcessing ? 'processing' : ''}`}
        onClick={handleMicClick}
        disabled={isProcessing}
        data-testid="mic-button"
        aria-label={isListening ? "Stop recording" : "Start recording"}
      >
        {isProcessing ? (
          <div className="spinner" />
        ) : isListening ? (
          <MicOff size={32} />
        ) : (
          <Mic size={32} />
        )}
        {isListening && <div className="pulse-ring" />}
      </button>

      {/* Error toast */}
      {error && (
        <div className="error-toast" data-testid="error-toast">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
}

export default App;
